// AdminOne Enterprise Telemetry — ORACLE-faithful (Hardening v1).
// NASA / Tesla / ATC visual. Truthful status only — no fabrication.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AdminOneEnterpriseTelemetry.css";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const ALLOWED_ROLES = new Set(["super_admin_root", "platform_admin", "admin", "super_admin", "executive", "ops"]);
const BLOCKED_ROLES = new Set(["individual", "patient", "family", "caregiver", "partner", "basic_staff"]);

function readAdminUser() {
  try {
    const raw = localStorage.getItem("carelogix_adminone_user");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function readAdminToken() {
  return localStorage.getItem("carelogix_adminone_token") || localStorage.getItem("adminone_jwt") || "";
}

function StatusPill({ status }) {
  const safe = (status || "").toUpperCase().replace(/[^A-Z_]/g, "");
  return (
    <span className={`ent-tel__statusPill status-${safe}`} data-testid={`ent-tel-status-${safe}`}>
      {status || "—"}
    </span>
  );
}

function Dot({ kind }) {
  return <span className={`ent-tel__dot dot-${kind}`} aria-hidden="true" />;
}

export default function AdminOneEnterpriseTelemetry() {
  const [adminUser] = useState(() => readAdminUser());
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [tick, setTick]         = useState(0);
  // Message 264 — Governance + Cross-Service + Audit additive surfaces
  const [governance, setGovernance] = useState(null);
  const [services, setServices]     = useState(null);
  const [auditSummary, setAuditSummary] = useState(null);
  // Phase 34A — Executive Reports + Phase 34B compliance widget + version lifecycle
  const [reports, setReports] = useState(null);
  const [reportTypes, setReportTypes] = useState(null);
  const [oracleCompliance, setOracleCompliance] = useState(null);
  const [versionAdoption, setVersionAdoption] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [reportError, setReportError] = useState(null);
  // Phase 36+ future — Cross-Service Ingestion + Scheduler
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerRuns, setSchedulerRuns] = useState(null);
  const [ingestLog, setIngestLog] = useState(null);
  const [triggeringSweep, setTriggeringSweep] = useState(false);
  const [ingestError, setIngestError] = useState(null);

  const allowed = useMemo(() => {
    if (!adminUser) return false;
    const role = (adminUser.role || "").toLowerCase();
    if (BLOCKED_ROLES.has(role)) return false;
    return ALLOWED_ROLES.has(role);
  }, [adminUser]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = { "Content-Type": "application/json" };
      const token = readAdminToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${BACKEND}/api/hardening/adminone/summary`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSummary(data);
      // Additive: pull governance + cross-service + audit surfaces (best-effort)
      try {
        const [gov, svc, aud, rpt, rtypes, oc, va, sched, runs, ilog] = await Promise.all([
          fetch(`${BACKEND}/api/adminone/governance/widgets`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/oracle/services/registry`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/adminone/enterprise-audit/summary`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/reports/history?limit=10`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/reports/types`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/adminone/governance/oracle-compliance`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/adminone/governance/version-adoption`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/cross-service/scheduler/status`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/cross-service/scheduler/runs?limit=5`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${BACKEND}/api/cross-service/ingest/log?limit=10`, { headers }).then(r => r.ok ? r.json() : null),
        ]);
        setGovernance(gov);
        setServices(svc);
        setAuditSummary(aud);
        setReports(rpt);
        setReportTypes(rtypes);
        setOracleCompliance(oc);
        setVersionAdoption(va);
        setSchedulerStatus(sched);
        setSchedulerRuns(runs);
        setIngestLog(ilog);
      } catch (_e) { /* additive — ignore */ }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);
  useEffect(() => {
    if (!allowed) return undefined;
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [allowed, load]);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 999), 1500);
    return () => clearInterval(id);
  }, []);

  if (!allowed) {
    return (
      <main className="ent-tel" data-testid="ent-tel-page">
        <div className="ent-tel__glow" aria-hidden="true" />
        <header className="ent-tel__header">
          <div>
            <div className="ent-tel__eyebrow">HARDENING v1 · ORACLE-FAITHFUL</div>
            <h1 className="ent-tel__title">AdminOne Enterprise Telemetry</h1>
          </div>
        </header>
        <section className="ent-tel__rbac" data-testid="ent-tel-rbac-block">
          <strong>Access restricted.</strong> This surface is reserved for super_admin_root, platform_admin, admin, executive, and ops roles.
          Individual, patient, family, caregiver, partner, and basic_staff roles are explicitly blocked from hardening telemetry.
          {!adminUser && <div style={{ marginTop: 6 }}>You are not signed in to AdminOne. Visit <code>/adminone/login</code>.</div>}
        </section>
      </main>
    );
  }

  const telemetry = summary?.telemetry || {};
  const domains   = telemetry?.domains?.items || [];
  const rollup    = telemetry?.domains?.by_status || {};
  const partners  = telemetry?.partners || [];
  const redis     = telemetry?.infrastructure?.redis || {};
  const bus       = telemetry?.infrastructure?.event_bus || {};
  const audit     = telemetry?.infrastructure?.audit_chain || {};
  const secrets   = telemetry?.secrets || {};
  const cont      = telemetry?.continuity || {};
  const sla       = telemetry?.sla || {};
  const readiness = summary?.readiness || {};
  const caveats   = summary?.truthful_caveats || [];

  return (
    <main className="ent-tel" data-testid="ent-tel-page">
      <div className="ent-tel__glow" aria-hidden="true" />
      <header className="ent-tel__header">
        <div>
          <div className="ent-tel__eyebrow">HARDENING v1 · ORACLE-FAITHFUL · TRUTHFUL STATUS</div>
          <h1 className="ent-tel__title" data-testid="ent-tel-title">AdminOne Enterprise Telemetry</h1>
          <p className="ent-tel__sub">15 ORACLE domains · infrastructure layer · partner connectors · deployment safety. Status vocabulary: ACTIVE / PARTIAL / MOCKED / AWAITING_KEYS / STUB_AWAITING_IMPLEMENTATION.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <span className="ent-tel__tier" data-testid="ent-tel-tier">TIER: {readiness?.tier || "—"}</span>
          <button type="button" onClick={load} className="ent-tel__refresh" data-testid="ent-tel-refresh">↻ Refresh</button>
        </div>
      </header>

      {error && <div className="ent-tel__err" data-testid="ent-tel-error">Telemetry error: {error}</div>}
      {loading && !summary && <div className="ent-tel__sub">Loading hardening telemetry…</div>}

      {/* KPI strip */}
      <section className="ent-tel__kpis" data-testid="ent-tel-kpis">
        <div className="ent-tel__kpi">
          <div className="ent-tel__kpiLbl">Readiness Score</div>
          <div className="ent-tel__kpiVal">{readiness?.score ?? "—"}<span style={{ fontSize: 12, color: "#8a8d93", marginLeft: 4 }}>/{readiness?.out_of ?? 100}</span></div>
          <div className="ent-tel__kpiSub">{readiness?.tier || ""}</div>
        </div>
        <div className="ent-tel__kpi">
          <div className="ent-tel__kpiLbl">Domains ACTIVE</div>
          <div className="ent-tel__kpiVal">{rollup?.ACTIVE ?? 0}<span style={{ fontSize: 12, color: "#8a8d93", marginLeft: 4 }}>/{telemetry?.domains?.total ?? 15}</span></div>
          <div className="ent-tel__kpiSub">PARTIAL {rollup?.PARTIAL ?? 0} · MOCKED {rollup?.MOCKED ?? 0} · AWAITING_KEYS {rollup?.AWAITING_KEYS ?? 0}</div>
        </div>
        <div className="ent-tel__kpi">
          <div className="ent-tel__kpiLbl">Redis</div>
          <div className="ent-tel__kpiVal" style={{ fontSize: 16 }}><Dot kind={redis.status === "ok" ? "ok" : "warn"} /> {redis.backend || "—"}</div>
          <div className="ent-tel__kpiSub">{redis.status || "—"}</div>
        </div>
        <div className="ent-tel__kpi">
          <div className="ent-tel__kpiLbl">Event Bus</div>
          <div className="ent-tel__kpiVal" style={{ fontSize: 16 }}><Dot kind={bus.status === "ok" ? "ok" : "warn"} /> {bus.backend || "—"}</div>
          <div className="ent-tel__kpiSub">log_size {bus.log_size ?? 0}</div>
        </div>
        <div className="ent-tel__kpi">
          <div className="ent-tel__kpiLbl">Audit Chain</div>
          <div className="ent-tel__kpiVal" style={{ fontSize: 16 }}><Dot kind={audit?.integrity ? "ok" : "crit"} /> {audit?.integrity ? "INTACT" : "BROKEN"}</div>
          <div className="ent-tel__kpiSub">len {audit?.length ?? 0}</div>
        </div>
        <div className="ent-tel__kpi">
          <div className="ent-tel__kpiLbl">Secrets</div>
          <div className="ent-tel__kpiVal" style={{ fontSize: 16 }}><Dot kind={secrets?.readiness === "PRODUCTION_READY" ? "ok" : "warn"} /> {secrets?.readiness || "—"}</div>
          <div className="ent-tel__kpiSub">{secrets?.present_count ?? 0}/{secrets?.total_required ?? 0} present</div>
        </div>
      </section>

      {/* Domains panel */}
      <section className="ent-tel__panel" data-testid="ent-tel-domains">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">ORACLE Domain Registry</h2>
          <span className="ent-tel__panelMeta">15 constitutional domains · truthful status</span>
        </header>
        <div className="ent-tel__grid">
          {domains.map((d) => (
            <article key={d.name} className="ent-tel__row" data-testid={`ent-tel-domain-${d.name}`}>
              <div className="ent-tel__rowMain">
                <span className="ent-tel__rowName">{d.name}</span>
                <span className="ent-tel__rowMeta">{d.classification} · {d.api_prefix || "(internal)"}</span>
                <span className="ent-tel__rowMeta">{d.notes}</span>
              </div>
              <StatusPill status={d.status} />
            </article>
          ))}
        </div>
      </section>

      {/* Partners panel */}
      <section className="ent-tel__panel" data-testid="ent-tel-partners">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">Partner Connectors</h2>
          <span className="ent-tel__panelMeta">Real integrations · credentials presence · circuit breaker</span>
        </header>
        <div className="ent-tel__grid">
          {partners.map((p) => (
            <article key={p.partner} className="ent-tel__row" data-testid={`ent-tel-partner-${p.partner}`}>
              <div className="ent-tel__rowMain">
                <span className="ent-tel__rowName">{p.partner}</span>
                <span className="ent-tel__rowMeta">creds {p.credentials_present ? "present" : "missing"} · circuit {p.circuit} · req {p.request_count} · ok {p.success_count} · fail {p.failure_count}</span>
                <span className="ent-tel__rowMeta">{p.notes}</span>
              </div>
              <StatusPill status={p.status} />
            </article>
          ))}
        </div>
      </section>

      {/* CEO Travel Command — additive launcher card */}
      <section className="ent-tel__panel" data-testid="ent-tel-ceo-travel">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">CEO Travel Command</h2>
          <span className="ent-tel__panelMeta">Dallas · Houston · NY ↔ Nigeria · Rwanda · Ghana · Kenya · Uganda</span>
        </header>
        <div className="ent-tel__grid">
          <article className="ent-tel__row" data-testid="ent-tel-ceo-travel-row">
            <div className="ent-tel__rowMain">
              <span className="ent-tel__rowName">CareLogix ORACLE™ CEO Travel Command™</span>
              <span className="ent-tel__rowMeta">18 lanes tracked · Delta rules engine · 8AM brief · 9AM email · TravelAgent™</span>
              <span className="ent-tel__rowMeta">
                <a href="/adminone/ceo-travel-command" data-testid="ent-tel-ceo-travel-link" style={{ color: "#7AC860", textDecoration: "underline" }}>
                  Open CEO Travel Command →
                </a>
              </span>
            </div>
            <StatusPill status="ACTIVE" />
          </article>
        </div>
      </section>

      {/* Continuity + SLA panel */}
      <section className="ent-tel__panel" data-testid="ent-tel-continuity">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">Continuity Threads & SLA</h2>
          <span className="ent-tel__panelMeta">live · tick {tick}</span>
        </header>
        <div className="ent-tel__kpis">
          <div className="ent-tel__kpi"><div className="ent-tel__kpiLbl">Threads</div><div className="ent-tel__kpiVal">{cont?.total_threads ?? 0}</div></div>
          <div className="ent-tel__kpi"><div className="ent-tel__kpiLbl">SLA Warnings</div><div className="ent-tel__kpiVal">{(sla?.warnings || []).length}</div></div>
          <div className="ent-tel__kpi"><div className="ent-tel__kpiLbl">SLA Breaches</div><div className="ent-tel__kpiVal">{(sla?.breaches || []).length}</div></div>
          <div className="ent-tel__kpi"><div className="ent-tel__kpiLbl">Fallback Events</div><div className="ent-tel__kpiVal">{telemetry?.fallbacks?.total ?? 0}</div></div>
        </div>
      </section>

      {/* Message 264 — Governance Telemetry (additive) */}
      <section className="ent-tel__panel" data-testid="ent-tel-governance">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">Governance Telemetry — Compliance Score</h2>
          <span className="ent-tel__panelMeta">
            language: en · canonical · truthful derivation
          </span>
        </header>
        {governance?.score ? (
          <>
            <div className="ent-tel__kpis">
              <div className="ent-tel__kpi" data-testid="gov-compliance-score">
                <div className="ent-tel__kpiLbl">Compliance Score</div>
                <div className="ent-tel__kpiVal">
                  {governance.score.score}
                  <span style={{ fontSize: 12, color: "#8a8d93", marginLeft: 4 }}>/100</span>
                </div>
                <div className="ent-tel__kpiSub">{governance.score.band}</div>
              </div>
              {(governance.widgets || []).filter(w => w.key !== "compliance_score").map((w) => (
                <div key={w.key} className="ent-tel__kpi" data-testid={`gov-widget-${w.key}`}>
                  <div className="ent-tel__kpiLbl">{w.label}</div>
                  <div className="ent-tel__kpiVal" style={{ fontSize: 18 }}>{typeof w.value === "number" ? w.value : "—"}</div>
                  <div className="ent-tel__kpiSub">{w.render_as}</div>
                </div>
              ))}
            </div>
            <div className="ent-tel__grid" style={{ marginTop: 12 }}>
              {(governance.score.components || []).map((c) => (
                <article key={c.component} className="ent-tel__row" data-testid={`gov-component-${c.component}`}>
                  <div className="ent-tel__rowMain">
                    <span className="ent-tel__rowName">{c.component}</span>
                    <span className="ent-tel__rowMeta">weight {c.weight} · score {c.score} · {c.status}</span>
                    <span className="ent-tel__rowMeta">{c.derivation}</span>
                  </div>
                  <StatusPill status={c.status === "OK" ? "ACTIVE" : c.status} />
                </article>
              ))}
            </div>
          </>
        ) : (
          <div className="ent-tel__sub">Governance score loading…</div>
        )}
      </section>

      {/* Message 264 — Cross-Service Registry (LIVE vs STUB_AWAITING_IMPLEMENTATION) */}
      <section className="ent-tel__panel" data-testid="ent-tel-cross-services">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">Cross-Service Compliance Registry</h2>
          <span className="ent-tel__panelMeta">
            {services?.counts?.live || 0} LIVE · {services?.counts?.stubs || 0} STUB_AWAITING_IMPLEMENTATION · avg live score {services?.avg_live_compliance_score ?? "—"}
          </span>
        </header>
        <div className="ent-tel__grid">
          {(services?.services || []).map((s) => (
            <article key={s.key} className="ent-tel__row" data-testid={`xsvc-${s.key}`}>
              <div className="ent-tel__rowMain">
                <span className="ent-tel__rowName">{s.name}</span>
                <span className="ent-tel__rowMeta">{s.kind} · telemetry {s.telemetry_source} · score {s.compliance_score} ({s.compliance_band})</span>
                <span className="ent-tel__rowMeta">{s.compliance_derivation}</span>
                {s.stub_note && <span className="ent-tel__rowMeta" style={{ color: "#E8A020" }}>{s.stub_note}</span>}
              </div>
              <StatusPill status={s.status} />
            </article>
          ))}
        </div>
      </section>

      {/* Message 264 — Enterprise Audit Summary */}
      <section className="ent-tel__panel" data-testid="ent-tel-audit-summary">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">Enterprise Audit · 24h Summary</h2>
          <span className="ent-tel__panelMeta">collection: enterprise_audit_events · canonical language: en</span>
        </header>
        <div className="ent-tel__kpis">
          <div className="ent-tel__kpi" data-testid="audit-total-24h">
            <div className="ent-tel__kpiLbl">Total Events</div>
            <div className="ent-tel__kpiVal">{auditSummary?.total_events ?? 0}</div>
          </div>
          <div className="ent-tel__kpi" data-testid="audit-critical-24h">
            <div className="ent-tel__kpiLbl">Critical + High</div>
            <div className="ent-tel__kpiVal">
              {(auditSummary?.by_severity?.critical || 0) + (auditSummary?.by_severity?.high || 0)}
            </div>
          </div>
          <div className="ent-tel__kpi">
            <div className="ent-tel__kpiLbl">By Scope</div>
            <div className="ent-tel__kpiVal" style={{ fontSize: 11, lineHeight: 1.6 }}>
              {Object.entries(auditSummary?.by_scope || {}).filter(([, v]) => v > 0).map(([k, v]) => `${k}:${v}`).join(" · ") || "—"}
            </div>
          </div>
          <div className="ent-tel__kpi">
            <div className="ent-tel__kpiLbl">Top Event</div>
            <div className="ent-tel__kpiVal" style={{ fontSize: 12 }}>
              {auditSummary?.top_events?.[0]?.event_type || "—"}
            </div>
            <div className="ent-tel__kpiSub">count {auditSummary?.top_events?.[0]?.count || 0}</div>
          </div>
        </div>
      </section>

      {/* Phase 34A — ORACLE Constitutional Compliance card (additive) */}
      <section className="ent-tel__panel" data-testid="ent-tel-oracle-compliance">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">ORACLE Constitutional Compliance</h2>
          <span className="ent-tel__panelMeta">
            current_version: {oracleCompliance?.current_version || "—"} · language: en
          </span>
        </header>
        <div className="ent-tel__kpis">
          <div className="ent-tel__kpi" data-testid="oc-status">
            <div className="ent-tel__kpiLbl">Compliance Status</div>
            <div className="ent-tel__kpiVal" style={{ fontSize: 16 }}>{oracleCompliance?.compliance_status || "—"}</div>
          </div>
          <div className="ent-tel__kpi" data-testid="oc-rate">
            <div className="ent-tel__kpiLbl">Compliance Rate</div>
            <div className="ent-tel__kpiVal">{oracleCompliance?.compliance_rate ?? 0}%</div>
          </div>
          <div className="ent-tel__kpi" data-testid="oc-users">
            <div className="ent-tel__kpiLbl">Acknowledged Users</div>
            <div className="ent-tel__kpiVal">{oracleCompliance?.acknowledged_users ?? 0}</div>
          </div>
          <div className="ent-tel__kpi" data-testid="oc-devices">
            <div className="ent-tel__kpiLbl">Acknowledged Devices</div>
            <div className="ent-tel__kpiVal">{oracleCompliance?.acknowledged_devices ?? 0}</div>
          </div>
          <div className="ent-tel__kpi" data-testid="oc-pending">
            <div className="ent-tel__kpiLbl">Devices Pending Ack</div>
            <div className="ent-tel__kpiVal">{oracleCompliance?.devices_pending_acknowledgement ?? 0}</div>
          </div>
        </div>
        {versionAdoption?.counts ? (
          <div className="ent-tel__grid" style={{ marginTop: 12 }}>
            {Object.entries(versionAdoption.counts).map(([v, c]) => (
              <article key={v} className="ent-tel__row" data-testid={`version-adoption-${v}`}>
                <div className="ent-tel__rowMain">
                  <span className="ent-tel__rowName">{v}</span>
                  <span className="ent-tel__rowMeta">
                    {c} acceptances · {(versionAdoption.percentages || {})[v] ?? 0}%
                  </span>
                </div>
                <StatusPill status={oracleCompliance?.current_version === v ? "CURRENT" : "PRIOR"} />
              </article>
            ))}
          </div>
        ) : null}
      </section>

      {/* Phase 34A — Executive Reports (additive) */}
      <section className="ent-tel__panel" data-testid="ent-tel-executive-reports">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">Executive Reports — PDF generation, audited</h2>
          <span className="ent-tel__panelMeta">version: {reportTypes?.version || "—"}</span>
        </header>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {(reportTypes?.report_types || []).map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`gen-report-${t.key}`}
              disabled={!!generating}
              onClick={async () => {
                setGenerating(t.key);
                setReportError(null);
                try {
                  const headers = { "Content-Type": "application/json" };
                  const token = readAdminToken();
                  if (token) headers.Authorization = `Bearer ${token}`;
                  const r = await fetch(`${BACKEND}/api/reports/generate`, {
                    method: "POST",
                    headers,
                    body: JSON.stringify({ report_type: t.key, actor: adminUser?.email || "adminone" }),
                  });
                  const j = await r.json();
                  if (!j?.ok) throw new Error(j?.detail || j?.error || "report_generation_failed");
                  // refresh history
                  const hr = await fetch(`${BACKEND}/api/reports/history?limit=10`, { headers }).then(rr => rr.json());
                  setReports(hr);
                } catch (e) {
                  setReportError(String(e.message || e));
                } finally {
                  setGenerating(null);
                }
              }}
              style={{
                padding: "6px 10px",
                fontSize: 11.5,
                border: "1px solid #c5a059",
                background: "transparent",
                color: "#c5a059",
                borderRadius: 999,
                cursor: generating ? "wait" : "pointer",
                opacity: generating && generating !== t.key ? 0.6 : 1,
              }}
            >
              {generating === t.key ? "Generating…" : `Generate · ${t.label}`}
            </button>
          ))}
        </div>
        {reportError ? (
          <div className="ent-tel__sub" style={{ color: "#E5484D" }} data-testid="report-error">{reportError}</div>
        ) : null}
        {reports?.reports?.length ? (
          <div className="ent-tel__grid">
            {reports.reports.map((r) => (
              <article key={r._id} className="ent-tel__row" data-testid={`report-${r._id.slice(0,8)}`}>
                <div className="ent-tel__rowMain">
                  <span className="ent-tel__rowName">{r.title}</span>
                  <span className="ent-tel__rowMeta">
                    type {r.report_type} · {(r.size_bytes / 1024).toFixed(1)} KB · v{r.version} · {r.created_at}
                  </span>
                </div>
                <a
                  href={`${BACKEND}/api/reports/download/${r._id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`download-${r._id.slice(0,8)}`}
                  style={{ color: "#c5a059", fontSize: 11, textDecoration: "underline" }}
                >
                  Download PDF
                </a>
              </article>
            ))}
          </div>
        ) : (
          <div className="ent-tel__sub">NO_DATA_AVAILABLE — generate a report to populate history</div>
        )}
      </section>

      {/* Phase 36+ — Cross-Service Ingestion Status (additive) */}
      <section className="ent-tel__panel" data-testid="ent-tel-ingestion-status">
        <header className="ent-tel__panelHead">
          <h2 className="ent-tel__panelTitle">Cross-Service Ingestion — Scheduler & Sweeps</h2>
          <span className="ent-tel__panelMeta">
            cron {schedulerStatus?.cron_hour != null ? `${String(schedulerStatus.cron_hour).padStart(2,"0")}:${String(schedulerStatus.cron_minute).padStart(2,"0")} ${schedulerStatus.timezone}` : "—"}
            {" · "}
            {schedulerStatus?.enabled ? (schedulerStatus.running ? "running" : "enabled (stopped)") : "DISABLED"}
          </span>
        </header>
        <div className="ent-tel__kpis">
          <div className="ent-tel__kpi" data-testid="ingest-scheduler-state">
            <div className="ent-tel__kpiLbl">Scheduler State</div>
            <div className="ent-tel__kpiVal" style={{ fontSize: 16 }}>
              {schedulerStatus?.running ? "RUNNING" : schedulerStatus?.enabled ? "ENABLED" : "DISABLED"}
            </div>
            <div className="ent-tel__kpiSub">job: {schedulerStatus?.job_id || "—"}</div>
          </div>
          <div className="ent-tel__kpi" data-testid="ingest-next-run">
            <div className="ent-tel__kpiLbl">Next Run</div>
            <div className="ent-tel__kpiVal" style={{ fontSize: 12 }}>
              {schedulerStatus?.next_run_time ? schedulerStatus.next_run_time.replace("T"," ").slice(0,19) : "—"}
            </div>
            <div className="ent-tel__kpiSub">UTC</div>
          </div>
          <div className="ent-tel__kpi" data-testid="ingest-last-status">
            <div className="ent-tel__kpiLbl">Last Run Status</div>
            <div className="ent-tel__kpiVal" style={{ fontSize: 16 }}>
              {schedulerStatus?.last_run?.status || "never_run"}
            </div>
            <div className="ent-tel__kpiSub">
              {schedulerStatus?.last_run?.triggered_by ? `by ${schedulerStatus.last_run.triggered_by}` : "—"}
            </div>
          </div>
          <div className="ent-tel__kpi" data-testid="ingest-last-live">
            <div className="ent-tel__kpiLbl">Last Sweep · LIVE / STUB</div>
            <div className="ent-tel__kpiVal">
              {schedulerStatus?.last_run?.live_services_processed ?? 0}
              <span style={{ fontSize: 12, color: "#8a8d93", margin: "0 4px" }}>/</span>
              {schedulerStatus?.last_run?.services_skipped_stub ?? 7}
            </div>
            <div className="ent-tel__kpiSub">services</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" }}>
          <button
            type="button"
            data-testid="ingest-trigger-sweep"
            disabled={triggeringSweep}
            onClick={async () => {
              setTriggeringSweep(true);
              setIngestError(null);
              try {
                const h = { "Content-Type": "application/json" };
                const token = readAdminToken();
                if (token) h.Authorization = `Bearer ${token}`;
                const r = await fetch(`${BACKEND}/api/cross-service/scheduler/trigger`, { method: "POST", headers: h });
                const j = await r.json();
                if (!j?.ok) throw new Error(j?.error || "trigger_failed");
                // refresh
                const [s, runs, ilog] = await Promise.all([
                  fetch(`${BACKEND}/api/cross-service/scheduler/status`, { headers: h }).then(rr => rr.json()),
                  fetch(`${BACKEND}/api/cross-service/scheduler/runs?limit=5`, { headers: h }).then(rr => rr.json()),
                  fetch(`${BACKEND}/api/cross-service/ingest/log?limit=10`, { headers: h }).then(rr => rr.json()),
                ]);
                setSchedulerStatus(s);
                setSchedulerRuns(runs);
                setIngestLog(ilog);
              } catch (e) {
                setIngestError(String(e.message || e));
              } finally {
                setTriggeringSweep(false);
              }
            }}
            style={{
              padding: "8px 14px",
              fontSize: 12,
              border: "1px solid #c5a059",
              background: triggeringSweep ? "rgba(197,160,89,0.15)" : "transparent",
              color: "#c5a059",
              borderRadius: 6,
              cursor: triggeringSweep ? "wait" : "pointer",
              letterSpacing: "0.05em",
            }}
          >
            {triggeringSweep ? "Sweeping…" : "▶ Trigger Manual Sweep"}
          </button>
          <span style={{ fontSize: 11, color: "#94a3b8", alignSelf: "center" }}>
            Truthful: STUB services produce zero rows. Only LIVE upstreams (env var set + /health OK) ingest data.
          </span>
        </div>

        {ingestError ? (
          <div className="ent-tel__sub" style={{ color: "#E5484D" }} data-testid="ingest-error">{ingestError}</div>
        ) : null}

        {schedulerRuns?.runs?.length ? (
          <>
            <div className="ent-tel__sub" style={{ marginTop: 4, marginBottom: 6 }}>Recent sweep runs:</div>
            <div className="ent-tel__grid">
              {schedulerRuns.runs.map((r) => (
                <article key={r._id} className="ent-tel__row" data-testid={`sweep-${r._id.slice(0,8)}`}>
                  <div className="ent-tel__rowMain">
                    <span className="ent-tel__rowName">{r.started_at?.replace("T"," ").slice(0,19) || "—"}</span>
                    <span className="ent-tel__rowMeta">
                      trigger {r.triggered_by} · status {r.status} · live {r.live_services_processed ?? 0} · skipped_stub {r.services_skipped_stub ?? 7}
                    </span>
                    {r.error ? <span className="ent-tel__rowMeta" style={{ color: "#E5484D" }}>error: {r.error}</span> : null}
                  </div>
                  <StatusPill status={r.status === "ok" ? "ACTIVE" : r.status?.toUpperCase() || "UNKNOWN"} />
                </article>
              ))}
            </div>
          </>
        ) : (
          <div className="ent-tel__sub">NO_DATA_AVAILABLE — no sweeps recorded yet</div>
        )}

        {ingestLog?.log?.length ? (
          <>
            <div className="ent-tel__sub" style={{ marginTop: 14, marginBottom: 6 }}>Recent ingestion log entries:</div>
            <div className="ent-tel__grid">
              {ingestLog.log.slice(0, 6).map((l) => (
                <article key={l._id} className="ent-tel__row" data-testid={`ingest-log-${l._id.slice(0,8)}`}>
                  <div className="ent-tel__rowMain">
                    <span className="ent-tel__rowName">{l.service_key}</span>
                    <span className="ent-tel__rowMeta">
                      outcome {l.outcome} · raw {l.raw_count ?? "—"} · ingested {l.ingested ?? 0} · skipped_no_id {l.skipped_no_id ?? 0}
                    </span>
                    <span className="ent-tel__rowMeta">{l.created_at}</span>
                  </div>
                  <StatusPill status={l.outcome === "ok" ? "ACTIVE" : (l.outcome || "UNKNOWN").toUpperCase()} />
                </article>
              ))}
            </div>
          </>
        ) : null}
      </section>

      {/* Truthful caveats */}
      <section className="ent-tel__caveats" data-testid="ent-tel-caveats">
        <h3 className="ent-tel__caveatsTitle">TRUTHFUL CAVEATS</h3>
        <ul>
          {caveats.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </section>
    </main>
  );
}
